import type { BaseServiceOptions, Pagination } from '@/core/types'
import {
  type TInvoice,
  invoicesServiceHandlers,
  serviceConfig,
  TInvoices,
} from '../../..'
import { useNotification, useServiceHandlers, defaultPagination } from '@/core'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { AxiosError } from 'axios'
import { toValue } from 'vue'

const { model } = serviceConfig.invoices

export const useInvoicesQuery = (options: BaseServiceOptions<Pagination>) => {
  const {
    currentPage = 1,
    enableQuery,
    filters,
    modelId,
    modelChildId,
    handlers,
  } = options

  const { errorHttpNotification } = useNotification()
  const serviceHandlers = useServiceHandlers<TInvoice>({
    model,
    currentPage: toValue(currentPage),
    filterParams: filters?.loadRecords ?? '',
  })

  const getTotalPercentage = (total: number, quantity: number) => {
    if (!total) return 0

    const result = (quantity / total) * 100

    return result >= 100 ? 100 : result
  }

  const deleteRecord = useMutation<void, AxiosError, TInvoice>(
    (payload) =>
      invoicesServiceHandlers.deleteRecord({
        modelChildId: payload.id,
        modelId,
      }),
    {
      onSuccess: (_, payload) => serviceHandlers.deleteRecord(payload),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const editRecord = useMutation<TInvoice, AxiosError, TInvoice>(
    (payload: TInvoice) =>
      invoicesServiceHandlers.editRecord(payload, {
        modelChildId: payload.id,
        modelId,
      }),
    {
      onSuccess: (payload) => serviceHandlers.updateRecord(payload),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const addRecord = useMutation<TInvoice, AxiosError, TInvoice>(
    (payload: TInvoice) =>
      invoicesServiceHandlers.addRecord(payload, { modelId }),
    {
      onSuccess: (payload) => serviceHandlers.addRecord(payload),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const loadRecords = useQuery<TInvoices, AxiosError>({
    queryKey: [model, currentPage, filters?.loadRecords ?? ''],
    queryFn: () =>
      invoicesServiceHandlers.loadRecords({
        dynamicFilters: filters?.loadRecords,
        page: currentPage,
        modelId,
      }),
    onError: (error) => errorHttpNotification(error),
    onSuccess: (data) => {
      if (data?.meta) {
        handlers?.onDataChange?.(data?.meta ?? defaultPagination)
      }
    },
    refetchOnWindowFocus: false,
    select: (data) => ({
      ...data,
      data: data.data.map((record) => ({
        ...record,
        total_amount_percentage: getTotalPercentage(
          record.amount ?? 0,
          record.amount_paid ?? 0,
        ),
      })),
    }),
    enabled: enableQuery?.loadRecords,
  })

  const loadRecord = useQuery<TInvoice, AxiosError>({
    queryKey: [model, modelId, modelChildId],
    queryFn: ({ queryKey }) =>
      invoicesServiceHandlers.loadRecord(
        {
          id: Number(queryKey[2]),
        },
        {
          modelId,
        },
      ),
    refetchOnWindowFocus: false,
    enabled: enableQuery?.loadRecord,
    onError: (error) => errorHttpNotification(error),
    onSuccess: (data) => {
      if (data.id) {
        handlers?.onLoadRecord?.(data)
      }
    },
    select: (data) => ({
      ...data,
      total_amount_percentage: getTotalPercentage(
        data.amount ?? 0,
        data.amount_paid ?? 0,
      ),
    }),
  })

  return {
    serviceHandlers,
    loadRecord,
    loadRecords,
    addRecord,
    editRecord,
    deleteRecord,
  }
}
